import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input, OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import {noop, Observable, Observer, of} from 'rxjs';
import {Parcel} from '../../../../_models/parcel';
import {Addresses} from '../../../../_models/address';
import {map, switchMap, tap} from 'rxjs/operators';
import {ApiService} from '../../../../_services/api.service';
import {TypeaheadMatch} from 'ngx-bootstrap/typeahead';
import {CustomFormControl} from '../controls/CustomFormControl';
import {FileOnlineViewModel} from '../../FileOnlineViewModel';
import {SplitterType} from '../../directives/number-splitter.directive';
import { UntypedFormControl } from '@angular/forms';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { FullParcel } from '../../../../_models/parcel';
import {ModalAlertMessage} from '../../models/ModalAlertMessage';

@Component({
    selector: 'app-address-search',
    templateUrl: './address-search.component.html',
    styleUrls: ['./address-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddressSearchComponent implements OnInit, AfterViewInit, OnDestroy {

  search: string;
  parcels: Observable<Parcel[]>;
  @Input() viewModel: FileOnlineViewModel;
  @Input() control!: CustomFormControl;
  selectedItem: Parcel | null;
  public readonly SplitterType = SplitterType;
  ainFormControl: UntypedFormControl;

  // Segmented control
  selectedOption = '';
  indicatorStyle: any = {}; // Stores translation & segment width
  @ViewChild('container') container!: ElementRef;
  options: { label: string, icon: string }[] = [
    { label: 'Address', icon: 'fas fa-home' },
    { label: 'AIN', icon: 'fas fa-hashtag' }
  ];
  private resizeObserver: ResizeObserver;

  constructor(private apiService: ApiService,
              private fb: UntypedFormBuilder) {
    this.ainFormControl = this.fb.control('', Validators.required);
  }

  ngOnInit(): void {
    this.parcels = new Observable((observer: Observer<string>) => {
      observer.next(this.search);
    }).pipe(
      switchMap((query: string) => {
        if (query) {
          return this.apiService.getAddress(query).pipe(
            map((data: Addresses) => data && data.Parcels || []),
            tap(() => noop, _ => {
            })
          );
        }

        return of([]);
      }));

    this.selectedOption = this.options[0].label;
    this.resizeObserver = new ResizeObserver((_: ResizeObserverEntry[]) => {
      this.updateSegmentIndicator();
    });
  }

  ngAfterViewInit(): void {
    this.updateSegmentIndicator();
    this.resizeObserver.observe(this.container.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  get reachedParcelLimit(): boolean {
    return this.viewModel.parcels.length === this.control.element.maximum;
  }

  handleTypeheadOnSelect(event: TypeaheadMatch): void {
    this.selectedItem = event.item;
    this.search = event.item.SitusStreet + ', ' +  event.item.SitusCity + ', ' +  event.item.SitusZipCode;
  }

  displayAddressLimitError(): void {
    this.displayError(
      'Address Limit Reached',
      `You are only able to attach ${this.control.element.maximum} parcels to this submission. For additional parcels, start a new submission.`
    );
  }

  onClickAdd(): void {
    if (this.selectedOption === 'Address') {
      this.onClickAddByAddress();
    } else {
      this.onClickAddByAin();
    }
  }

  onClickAddByAddress(): void {
    if (this.reachedParcelLimit) {
      this.displayAddressLimitError();
      return;
    }

    if (this.selectedItem) {
      // Parcels added by address do not have all info required
      this.apiService.getParcel(this.selectedItem.AIN).subscribe(fullParcel => {
        this.handleParcelAdd(fullParcel.Parcel);
      });
      return;
    }

    const message = `
    <p>Type your address in the search field to see auto-complete suggestions. Click the suggestion that best matches your address. Then click \"Add\" to include the parcel in your submission.</p>
    <p>If your address does not appear in the results, you can try using the AIN associated with your parcel or submit a paper form.</p>
    `;
    this.displayError('Invalid Address', message);
  }

  onClickAddByAin(): void {
    const lookupValue = this.ainFormControl.value;

    if (!lookupValue) {
      this.displayError('No AIN Provided', 'The AIN field cannot be empty. Please type a 10 digit AIN input and try again.');
      return;
    }

    const strippedAin = lookupValue.replace(/-/g, '');

    if (strippedAin.length !== 10) {
      this.displayError('Invalid AIN Provided', 'Please type a 10 digit AIN input and try again.');
      return;
    }

    if (this.reachedParcelLimit) {
      this.displayAddressLimitError();
      return;
    }

    this.apiService.getParcel(strippedAin).subscribe((res: FullParcel) => {
      if (res.Parcel) {
        this.handleParcelAdd(res.Parcel);
        this.ainFormControl.reset();
        return;
      }

      this.handleNullParcel(lookupValue);
    }, _ => {
      this.handleNullParcel(lookupValue);
    });
  }

  handleNullParcel(lookupValue: string): void {
    this.displayError('Invalid AIN Provided',
      `The provided AIN ${lookupValue} was not found in our records. Please check the value and try again.`);
  }

  displayError(title: string, message: string): void {
    this.viewModel.modalTriggerSubject.next(new ModalAlertMessage(title, message));
  }

  handleParcelAdd(parcel: Parcel): void {
    this.search = ''; // Reset the string
    this.selectedItem = null;

    if (this.parcelAlreadyAdded(parcel)) {
      this.displayError('Duplicate Parcel', 'You have already attached this parcel to your submission.');
      return;
    }

    if (parcel.SitusStreet.trim().length === 0) {
      this.displayError('No Address Found', `The provided AIN is valid, but the situs address could not be found. Please submit a paper form for the parcel associated with the AIN: ${parcel.AIN}.`);
      return;
    }

    this.control.setValue(parcel);
    this.viewModel.addParcel(parcel);
    this.viewModel.cacheParcel(parcel.AIN, parcel);
  }

  parcelAlreadyAdded(parcelToAdd: Parcel): boolean {
    for (const parcel of this.viewModel.parcels) {
      if (parcelToAdd.AIN === parcel.AIN) {
        return true;
      }
    }

    return false;
  }

  updateSegmentIndicator(): void {
    const index = this.options.findIndex(opt => opt.label === this.selectedOption);
    if (!this.container || index === -1) {
      this.delaySegmentUpdate();
      return;
    }

    const containerElement = this.container.nativeElement;
    const segments = containerElement.querySelectorAll('.segment');
    const selectedSegment = segments[index];

    if (!selectedSegment) {
      this.delaySegmentUpdate();
      return;
    }

    this.indicatorStyle = {
      width: `${selectedSegment.offsetWidth}px`,
      transform: `translateX(${selectedSegment.offsetLeft}px)`
    };
  }

  delaySegmentUpdate(): void {
    setTimeout(() => {
      this.updateSegmentIndicator();
    }, 50);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateSegmentIndicator();
  }

  onClickSegment(label: string): void {
    this.selectedOption = label;
    this.search = '';
    this.updateSegmentIndicator();
  }
}
